/**
 * Product Form Wizard schemas (S1.3)
 *
 * zodResolver schema + per-step trigger() field lists + request mappers for
 * `ProductFormWizard`. Error messages are i18n KEYS (translated at display —
 * house schema convention), present in BOTH en + zh products.json.
 *
 * Requiredness contract (two-tier, S1.2 · OQ-ME2):
 * - Must authority = 0708 xlsx **col-J** (74 Must by label); col K is stale.
 * - zod hard-requires ONLY the create-8 set: Identity (sku, displayName) +
 *   Classification (subsidiaryId, categoryId) + Units (unitsTypeId +
 *   stock/purchase/sale units). Red asterisks = these exactly.
 * - Tier-2 readiness fields (3 descriptions, basePrice, costingMethod) are
 *   OPTIONAL to save — they gate 產品就緒 Product Ready on read as enrichment
 *   only, never the save and never a third MoSCoW.
 * - Collections (customer PNs, vendor lines, images, fitment years) are
 *   OPTIONAL (house wizard-collections rule).
 * - S2.1 基本資料 fields (variant / extractYear / faSubCategory /
 *   ageStartYear / ageEndYear / minQtyUnit / maxQtyUnit / itemDefinedCostUnit)
 *   live IN this schema + defaults + hydrate + mappers — the S0.3 drift class
 *   (rendered field missing from schema ⇒ silently dropped on save) must not
 *   recur. `ageGroup` is deliberately absent: C108 system-computed.
 */

import { z } from 'zod';
import type { Path } from 'react-hook-form';
import type {
  CreateProductRequest,
  Product,
  ProductCostingMethod,
  ProductCreateOnlyField,
  ProductImageSlot,
  UpdateProductRequest,
} from '@/types/api/product';
import { MAX_PRODUCT_IMAGES } from '@/types/api/product';

/**
 * 單位 family (W7-B3) — the ONE unit option list shared by the product unit
 * selects (stock/purchase/sale) AND the vendor-line 訂購單位 capture. No
 * reference endpoint exists yet (wizard followUps); single home so the two
 * surfaces cannot drift.
 */
export const UNIT_VALUES = ['PCS', 'SET', 'PAIR', 'BOX', 'CTN', 'KG'] as const;

// ---------------------------------------------------------------------------
// Row schemas (collection sub-forms — each edited via a row modal)
// ---------------------------------------------------------------------------

export const fitmentYearRowSchema = z
  .object({
    id: z.string(),
    yearFrom: z
      .number({
        required_error: 'products:validation.yearFromRequired',
        invalid_type_error: 'products:validation.yearFromRequired',
      })
      .int('products:validation.yearInvalid')
      .min(1950, 'products:validation.yearInvalid')
      .max(2100, 'products:validation.yearInvalid'),
    yearTo: z
      .number()
      .int('products:validation.yearInvalid')
      .min(1950, 'products:validation.yearInvalid')
      .max(2100, 'products:validation.yearInvalid')
      .nullable()
      .optional(),
    variant: z.string().trim().optional(),
  })
  .refine((row) => row.yearTo == null || row.yearTo >= row.yearFrom, {
    message: 'products:validation.yearRange',
    path: ['yearTo'],
  });

export type FitmentYearRow = z.infer<typeof fitmentYearRowSchema>;

export const customerPartNumberRowSchema = z.object({
  id: z.string(),
  customerId: z
    .number({
      required_error: 'products:validation.customerRequired',
      invalid_type_error: 'products:validation.customerRequired',
    })
    .min(1, 'products:validation.customerRequired'),
  customerName: z.string(),
  partNumber: z.string().trim().min(1, 'products:validation.partNumberRequired'),
  description: z.string().trim().optional(),
});

export type CustomerPartNumberRow = z.infer<typeof customerPartNumberRowSchema>;

/**
 * 備件料號 row (2026-08-27 §5) — sibling of customerPartNumberRowSchema.
 * Deliberately NO customer/vendor key: the spare-part alias belongs to the
 * product itself (D8 external-identifier posture).
 */
export const sparePartNumberRowSchema = z.object({
  id: z.string(),
  partNumber: z.string().trim().min(1, 'products:validation.partNumberRequired'),
  description: z.string().trim().optional(),
});

export type SparePartNumberRow = z.infer<typeof sparePartNumberRowSchema>;

export const vendorLineRowSchema = z.object({
  id: z.string(),
  vendorId: z
    .number({
      required_error: 'products:validation.vendorRequired',
      invalid_type_error: 'products:validation.vendorRequired',
    })
    .min(1, 'products:validation.vendorRequired'),
  vendorName: z.string(),
  vendorSku: z.string().trim().optional(),
  unitCost: z
    .number({
      required_error: 'products:validation.unitCostRequired',
      invalid_type_error: 'products:validation.unitCostRequired',
    })
    .min(0, 'products:validation.nonNegative'),
  currency: z.string().min(1, 'products:validation.currencyRequired'),
  /**
   * 訂購單位 (W7-B3) — optional capture from UNIT_VALUES; display-only, NO
   * qty conversion against stockUnit (decision-gated — Q5).
   */
  orderUnit: z.string().nullable().optional(),
  isPrimary: z.boolean(),
  leadTimeDays: z
    .number()
    .int('products:validation.nonNegative')
    .min(0, 'products:validation.nonNegative')
    .nullable()
    .optional(),
  /** True when the line already exists on the API (edit mode) — delete is local-rows-only. */
  isExisting: z.boolean(),
});

export type VendorLineRow = z.infer<typeof vendorLineRowSchema>;

export const imageSlotRowSchema = z.object({
  slot: z.number().int().min(1).max(MAX_PRODUCT_IMAGES),
  url: z.string().trim().min(1, 'products:validation.imageUrlRequired'),
  caption: z.string().trim().optional(),
});

export type ImageSlotRow = z.infer<typeof imageSlotRowSchema>;

// ---------------------------------------------------------------------------
// Wizard schema
// ---------------------------------------------------------------------------

const COSTING_METHODS = ['FIFO', 'MOVING_AVERAGE', 'STANDARD', 'WEIGHTED_AVERAGE'] as const;

const optionalDim = z.number().min(0, 'products:validation.nonNegative').nullable().optional();

/** Model-year style input (extract year / vehicle start/end year — S2.1). */
const optionalYear = z
  .number()
  .int('products:validation.yearInvalid')
  .min(1950, 'products:validation.yearInvalid')
  .max(2100, 'products:validation.yearInvalid')
  .nullable()
  .optional();

/**
 * Editable accounting placeholders (R217). Every NON-blocked `accounting.*`
 * registry row MUST appear here AND in `productWizardDefaults` AND in
 * `hydrateFromProduct` — zodResolver `.parse()` strips unknown keys, so a
 * missing entry silently drops the value on save (S0.3 drift class; guarded
 * by `productAccountingRoundTrip.test.ts`).
 */
export const accountingPlaceholdersSchema = z.object({
  incomeAccountId: z.string().trim().optional(),
  cogsAccountId: z.string().trim().optional(),
  assetAccountId: z.string().trim().optional(),
  gainLossAccountId: z.string().trim().optional(),
  priceVarianceAccountId: z.string().trim().optional(),
  quantityVarianceAccountId: z.string().trim().optional(),
  exchangeRateVarianceAccountId: z.string().trim().optional(),
  // 0708-J Must trio the codex-era schema missed (S0.3 drift bugfix)
  customerReturnVarianceAccountId: z.string().trim().optional(),
  vendorReturnVarianceAccountId: z.string().trim().optional(),
  dropshipExpenseAccountId: z.string().trim().optional(),
  deferredRevenueAccountId: z.string().trim().optional(),
  vendorBillPriceVarianceAccountId: z.string().trim().optional(),
  vendorBillQuantityVarianceAccountId: z.string().trim().optional(),
  vendorBillExchangeRateVarianceAccountId: z.string().trim().optional(),
  // DD!227–229 Sterling-rider trio (S8.1) — registry `blocked` lifted
  // together with this coverage, per the S0.1 handshake.
  intercompanyIncomeAccountId: z.string().trim().optional(),
  intercompanyCogsAccountId: z.string().trim().optional(),
  taxScheduleId: z.string().trim().optional(),
});

export const productWizardSchema = z
  .object({
    // -- Step ① 識別與分類 (create-required) --------------------------------
    sku: z.string().trim().min(1, 'products:validation.skuRequired'),
    displayName: z.string().trim().min(1, 'products:validation.displayNameRequired'),
    subsidiaryId: z.string().min(1, 'products:validation.subsidiaryRequired'),
    categoryId: z.string().min(1, 'products:validation.categoryRequired'),
    includeChildren: z.boolean(),
    // DD!21 停用 Inactive (0708-J Must, G-2169-C-021) — the create-wizard
    // header Switch, default ON/Active. Must-visible ≠ required-to-save:
    // never an asterisk, registry `requiredAtCreate` stays false. In the
    // schema (not wizard-local state) per the S0.3 drift-class guard.
    isActive: z.boolean(),

    // -- Step ② 單位與規格 (units create-required; physical optional) --------
    unitsTypeId: z.string().min(1, 'products:validation.unitsTypeRequired'),
    stockUnit: z.string().min(1, 'products:validation.stockUnitRequired'),
    purchaseUnit: z.string().min(1, 'products:validation.purchaseUnitRequired'),
    saleUnit: z.string().min(1, 'products:validation.saleUnitRequired'),
    lengthMm: optionalDim,
    widthMm: optionalDim,
    heightMm: optionalDim,
    weightKg: optionalDim,
    fitmentMakes: z.array(z.string()),
    fitmentModels: z.array(z.string()),
    fitmentYears: z.array(fitmentYearRowSchema),
    // S2.1 Car Details fitment group (0708-J Must): DD!31 product-level
    // variant + DD!32 extract year. In the SCHEMA (not wizard-local state) so
    // the S0.3 drift class cannot recur — zodResolver `.parse()` strips any
    // rendered field missing here.
    variant: z.string().trim().optional(),
    extractYear: optionalYear,
    mpn: z.string().trim().optional(),
    countryOfManufacture: z.string().trim().optional(),
    // S2.1 manufacturing (0708-J Must): DD!69 FA Sub Category.
    faSubCategory: z.string().trim().optional(),
    // S2.1 C108 age family — the USER enters the vehicle year range; `ageGroup`
    // itself is system-computed (pure `computeAgeGroup`) and never a form field.
    ageStartYear: optionalYear,
    ageEndYear: optionalYear,

    // -- Step ③ 描述與識別碼 (Tier-2 / optional) ----------------------------
    salesDescription: z.string().trim().optional(),
    purchaseDescription: z.string().trim().optional(),
    customsDescription: z.string().trim().optional(),
    oemNo: z.string().trim().optional(),
    upcCode: z.string().trim().optional(),
    customerPartNumbers: z.array(customerPartNumberRowSchema),
    sparePartNumbers: z.array(sparePartNumberRowSchema),
    vendorLines: z.array(vendorLineRowSchema),
    images: z.array(imageSlotRowSchema).max(MAX_PRODUCT_IMAGES, 'products:validation.imagesMax'),

    // -- Step ④ 商務與會計 (Tier-2 / optional) ------------------------------
    costingMethod: z.enum(COSTING_METHODS).nullable(),
    basePrice: z.number().min(0, 'products:validation.nonNegative').nullable(),
    currency: z.string().min(1, 'products:validation.currencyRequired'),
    minQty: optionalDim,
    maxQty: optionalDim,
    itemDefinedCost: optionalDim,
    // S2.1 unit qualifiers (0708-J Must): DD!100 / DD!102 / DD!98 — sourced
    // from the existing units vocabulary (`unitsTypeId` family).
    minQtyUnit: z.string().trim().optional(),
    maxQtyUnit: z.string().trim().optional(),
    itemDefinedCostUnit: z.string().trim().optional(),
    accounting: accountingPlaceholdersSchema,
  })
  .refine((data) => data.minQty == null || data.maxQty == null || data.maxQty >= data.minQty, {
    message: 'products:validation.maxQtyBelowMin',
    path: ['maxQty'],
  })
  .refine(
    (data) =>
      data.ageStartYear == null || data.ageEndYear == null || data.ageEndYear >= data.ageStartYear,
    {
      message: 'products:validation.ageYearRange',
      path: ['ageEndYear'],
    }
  );

export type ProductWizardFormData = z.infer<typeof productWizardSchema>;

export const productWizardDefaults: ProductWizardFormData = {
  sku: '',
  displayName: '',
  subsidiaryId: '',
  categoryId: '',
  includeChildren: false,
  isActive: true, // DD!21 — default Active on create (G-2169-C-021)
  unitsTypeId: 'UT-QTY',
  stockUnit: 'PCS',
  purchaseUnit: 'PCS',
  saleUnit: 'PCS',
  lengthMm: null,
  widthMm: null,
  heightMm: null,
  weightKg: null,
  fitmentMakes: [],
  fitmentModels: [],
  fitmentYears: [],
  variant: '',
  extractYear: null,
  mpn: '',
  countryOfManufacture: '',
  faSubCategory: '',
  ageStartYear: null,
  ageEndYear: null,
  salesDescription: '',
  purchaseDescription: '',
  customsDescription: '',
  oemNo: '',
  upcCode: '',
  customerPartNumbers: [],
  sparePartNumbers: [],
  vendorLines: [],
  images: [],
  costingMethod: null,
  basePrice: null,
  currency: 'AUD',
  minQty: null,
  maxQty: null,
  itemDefinedCost: null,
  minQtyUnit: '',
  maxQtyUnit: '',
  itemDefinedCostUnit: '',
  accounting: {
    incomeAccountId: '',
    cogsAccountId: '',
    assetAccountId: '',
    gainLossAccountId: '',
    priceVarianceAccountId: '',
    quantityVarianceAccountId: '',
    exchangeRateVarianceAccountId: '',
    customerReturnVarianceAccountId: '',
    vendorReturnVarianceAccountId: '',
    dropshipExpenseAccountId: '',
    deferredRevenueAccountId: '',
    vendorBillPriceVarianceAccountId: '',
    vendorBillQuantityVarianceAccountId: '',
    vendorBillExchangeRateVarianceAccountId: '',
    intercompanyIncomeAccountId: '',
    intercompanyCogsAccountId: '',
    taxScheduleId: '',
  },
};

// ---------------------------------------------------------------------------
// Per-step trigger() field lists (forward-only validation, S0.3 contract)
// ---------------------------------------------------------------------------

export const PRODUCT_WIZARD_STEP_FIELDS: ReadonlyArray<ReadonlyArray<Path<ProductWizardFormData>>> =
  [
    ['sku', 'displayName', 'subsidiaryId', 'categoryId', 'isActive'],
    [
      'unitsTypeId',
      'stockUnit',
      'purchaseUnit',
      'saleUnit',
      'lengthMm',
      'widthMm',
      'heightMm',
      'weightKg',
      'fitmentYears',
      'variant',
      'extractYear',
      'faSubCategory',
      'ageStartYear',
      'ageEndYear',
    ],
    [
      'salesDescription',
      'purchaseDescription',
      'customsDescription',
      'oemNo',
      'upcCode',
      'customerPartNumbers',
      'sparePartNumbers',
      'vendorLines',
      'images',
    ],
    [
      'costingMethod',
      'basePrice',
      'currency',
      'minQty',
      'maxQty',
      'itemDefinedCost',
      'minQtyUnit',
      'maxQtyUnit',
      'itemDefinedCostUnit',
    ],
    [],
  ];

// ---------------------------------------------------------------------------
// Mappers — form ⇄ API contracts
// ---------------------------------------------------------------------------

const emptyToUndefined = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const nullToUndefined = (value: number | null | undefined): number | undefined =>
  value == null ? undefined : value;

function mapImages(rows: ImageSlotRow[]): ProductImageSlot[] {
  return rows.slice(0, MAX_PRODUCT_IMAGES).map((row, index) => ({
    slot: (index + 1) as ProductImageSlot['slot'],
    url: row.url.trim(),
    caption: emptyToUndefined(row.caption),
  }));
}

function mapFitment(data: ProductWizardFormData) {
  if (
    data.fitmentMakes.length === 0 &&
    data.fitmentModels.length === 0 &&
    data.fitmentYears.length === 0
  ) {
    return undefined;
  }
  return {
    makes: data.fitmentMakes,
    models: data.fitmentModels,
    years: data.fitmentYears.map((row) => ({
      yearFrom: row.yearFrom,
      yearTo: row.yearTo ?? undefined,
      variant: emptyToUndefined(row.variant),
    })),
  };
}

function mapAccounting(data: ProductWizardFormData) {
  const entries = Object.entries(data.accounting).map(
    ([key, value]) => [key, emptyToUndefined(value) ?? null] as const
  );
  if (entries.every(([, value]) => value === null)) return undefined;
  return Object.fromEntries(entries) as CreateProductRequest['accounting'];
}

/** Request-builder options (RULING-2 2026-08-29, flag-derived at the call site). */
export interface ProductRequestOptions {
  /**
   * RULING-2: currency is system-set — like `ageGroup` (C108) above, the
   * field is deliberately NEVER in the body; the server assigns the company
   * base currency (mock: `mocks/handlers/product.ts` create default 'AUD').
   * Flag-off callers omit the option and keep sending the picked value.
   */
  currencySystemSet?: boolean;
}

/** Vendor lines are NOT in the create body — they post through /products/{id}/vendors. */
export function toCreateProductRequest(
  data: ProductWizardFormData,
  opts?: ProductRequestOptions
): CreateProductRequest {
  return {
    sku: data.sku.trim(),
    displayName: data.displayName.trim(),
    subsidiaryId: data.subsidiaryId,
    categoryId: data.categoryId,
    includeChildren: data.includeChildren,
    unitsTypeId: data.unitsTypeId,
    stockUnit: data.stockUnit,
    purchaseUnit: data.purchaseUnit,
    saleUnit: data.saleUnit,
    salesDescription: emptyToUndefined(data.salesDescription),
    purchaseDescription: emptyToUndefined(data.purchaseDescription),
    customsDescription: emptyToUndefined(data.customsDescription),
    oemNo: emptyToUndefined(data.oemNo) ?? null,
    mpn: emptyToUndefined(data.mpn) ?? null,
    upcCode: emptyToUndefined(data.upcCode) ?? null,
    customerPartNumbers: data.customerPartNumbers.map((row) => ({
      id: row.id,
      customerId: row.customerId,
      customerName: row.customerName,
      partNumber: row.partNumber.trim(),
      description: emptyToUndefined(row.description),
    })),
    sparePartNumbers: data.sparePartNumbers.map((row) => ({
      id: row.id,
      partNumber: row.partNumber.trim(),
      description: emptyToUndefined(row.description),
    })),
    images: mapImages(data.images),
    fitment: mapFitment(data),
    // S2.1 Car Details + manufacturing + C108 age family. `ageGroup` is
    // deliberately NEVER in the body — it is system-computed from the year
    // range (C108 【MUST】); handlers ignore a caller-sent value anyway.
    variant: emptyToUndefined(data.variant),
    extractYear: nullToUndefined(data.extractYear),
    faSubCategory: emptyToUndefined(data.faSubCategory),
    ageStartYear: nullToUndefined(data.ageStartYear),
    ageEndYear: nullToUndefined(data.ageEndYear),
    countryOfManufacture: emptyToUndefined(data.countryOfManufacture),
    lengthMm: nullToUndefined(data.lengthMm),
    widthMm: nullToUndefined(data.widthMm),
    heightMm: nullToUndefined(data.heightMm),
    weightKg: nullToUndefined(data.weightKg),
    costingMethod: data.costingMethod,
    basePrice: data.basePrice,
    // RULING-2: system-set ⇒ never in the body (the C108 `ageGroup`
    // precedent above) — the server assigns the company base currency.
    ...(opts?.currencySystemSet ? {} : { currency: data.currency }),
    minQty: nullToUndefined(data.minQty),
    maxQty: nullToUndefined(data.maxQty),
    itemDefinedCost: nullToUndefined(data.itemDefinedCost),
    minQtyUnit: emptyToUndefined(data.minQtyUnit),
    maxQtyUnit: emptyToUndefined(data.maxQtyUnit),
    itemDefinedCostUnit: emptyToUndefined(data.itemDefinedCostUnit),
    accounting: mapAccounting(data),
  };
}

/**
 * The wizard's EDIT save body.
 *
 * `return toCreateProductRequest(data)` is what this used to be, and it is the
 * line D-4 and D-5 came out of (2026-08-10): the create set is not the mutable
 * set, so every edit save carried `subsidiaryId` and `unitsTypeId` —
 * `ProductUpdateInput` declares neither. `unitsTypeId` reached the wire and the
 * graph refused the whole mutation (HTTP 400); `subsidiaryId` was dropped by
 * the facade and the save reported success over an unmoved row.
 *
 * The omission is DERIVED from `PRODUCT_CREATE_ONLY_FIELDS`, which is typed as
 * `ProductCreateOnlyField[]` — so if the contract ever makes one of them
 * patchable, deleting it from the union is a compile error here until this
 * list follows, rather than a silently stale hand-written pick.
 */
export const PRODUCT_CREATE_ONLY_FIELDS: readonly ProductCreateOnlyField[] = [
  'subsidiaryId',
  'unitsTypeId',
] as const;

export function toUpdateProductRequest(
  data: ProductWizardFormData,
  opts?: ProductRequestOptions
): UpdateProductRequest {
  const body: Record<string, unknown> = {
    ...toCreateProductRequest(data, opts),
    // DEFECT-A (FIELD-SIGNOFF F4 §2-Fix4, rows 7/31/69/97/99/101) — wizard
    // EDIT clears must reach the PATCH body. The create mapper's omission
    // (emptyToUndefined / nullToUndefined) is correct for POST, but the apply
    // seat keeps any omitted key (`rules/product.ts` `!== undefined` guards),
    // so a cleared field silently kept its stored value. Doctrine: strings
    // clear with `''` (S2.1) and amounts clear with `null` (sealed W7
    // CORRECTED 2026-08-27 — "Stop `?? undefined` omit"). `ageGroup` (C108)
    // and the RULING-2 currency omission above stay untouched — system-set,
    // never in the body.
    salesDescription: data.salesDescription ?? '',
    purchaseDescription: data.purchaseDescription ?? '',
    customsDescription: data.customsDescription ?? '',
    variant: data.variant ?? '',
    faSubCategory: data.faSubCategory ?? '',
    // D-X1 (F4 §2-Fix1) — countryOfManufacture rides the same string-clear
    // block; the modal side clears via `buildClassificationPatch`.
    countryOfManufacture: data.countryOfManufacture ?? '',
    minQty: data.minQty ?? null,
    maxQty: data.maxQty ?? null,
    itemDefinedCost: data.itemDefinedCost ?? null,
    // DD!21 — NO `isActive` (D-3, 2026-08-10). This body used to carry it, on
    // the belief that "activation rides the PATCH contract". That sentence is
    // false and was false when it was written: `ProductUpdateInput` declares
    // no `isActive`, `toMutableInput` is an allowlist that does not carry it,
    // so the key was dropped at the seam and the empty PATCH answered
    // `userErrors: []` — a write that could neither land nor fail. The MSW
    // PATCH handler DID apply it, so the mock agreed with this mapper and
    // disagreed with the server. `UpdateProductRequest` no longer declares the
    // field, and activation is settable ONLY through
    // POST /products/{id}/set-active (`productApi.setActive`). The wizard's
    // create seat routes an explicit Inactive choice there right after POST
    // (ProductFormWizard.handleCreate, G-2169-C-021); the edit seat renders no
    // Switch at all — the detail header owns that toggle and it, too, goes
    // through `setActive`. Nothing is lost by the omission: there was never a
    // live path for it to be lost from.
  };
  // D-4 + D-5 — strip the create-only fields the PATCH contract cannot carry.
  for (const field of PRODUCT_CREATE_ONLY_FIELDS) delete body[field];
  return body as UpdateProductRequest;
}

/**
 * Synthetic read-shape for `computeProductReadiness` — previews the two-tier
 * gate (產品就緒) from live form values on the Review step.
 */
export function toReadinessPreview(data: ProductWizardFormData): Product {
  const request = toCreateProductRequest(data);
  const now = new Date().toISOString();
  return {
    id: 'preview',
    internalProductId: '',
    sku: request.sku,
    displayName: request.displayName,
    subsidiaryId: request.subsidiaryId,
    includeChildren: request.includeChildren,
    categoryId: request.categoryId,
    unitsTypeId: request.unitsTypeId ?? 'UT-QTY',
    stockUnit: request.stockUnit ?? 'PCS',
    purchaseUnit: request.purchaseUnit ?? 'PCS',
    saleUnit: request.saleUnit ?? 'PCS',
    salesDescription: request.salesDescription,
    purchaseDescription: request.purchaseDescription,
    customsDescription: request.customsDescription,
    oemNo: request.oemNo,
    mpn: request.mpn,
    upcCode: request.upcCode,
    customerPartNumbers: request.customerPartNumbers,
    sparePartNumbers: request.sparePartNumbers,
    vendorLines: data.vendorLines.map((row) => ({
      id: row.id,
      productId: 'preview',
      vendorId: row.vendorId,
      vendorName: row.vendorName,
      vendorSku: emptyToUndefined(row.vendorSku),
      unitCost: row.unitCost,
      currency: row.currency,
      orderUnit: row.orderUnit ?? undefined,
      isPrimary: row.isPrimary,
      isPreferred: row.isPrimary,
      leadTimeDays: row.leadTimeDays ?? undefined,
    })),
    images: request.images,
    fitment: request.fitment,
    variant: request.variant,
    extractYear: request.extractYear,
    faSubCategory: request.faSubCategory,
    ageStartYear: request.ageStartYear,
    ageEndYear: request.ageEndYear,
    countryOfManufacture: request.countryOfManufacture,
    lengthMm: request.lengthMm,
    widthMm: request.widthMm,
    heightMm: request.heightMm,
    weightKg: request.weightKg,
    costingMethod: request.costingMethod,
    basePrice: request.basePrice,
    currency: request.currency,
    // Null-clear request fields (ef363ca) collapse to absent on the entity —
    // same `?? undefined` convention as applyCanonicalFields.
    minQty: request.minQty ?? undefined,
    maxQty: request.maxQty ?? undefined,
    itemDefinedCost: request.itemDefinedCost ?? undefined,
    minQtyUnit: request.minQtyUnit ?? undefined,
    maxQtyUnit: request.maxQtyUnit ?? undefined,
    itemDefinedCostUnit: request.itemDefinedCostUnit ?? undefined,
    accounting: request.accounting,
    // DD!21 — wired from the header Switch (G-2169-C-021); this used to be the
    // create path's `isActive: true` hardcode.
    isActive: data.isActive,
    createdAt: now,
    updatedAt: now,
    // Deprecated legacy mirrors required by the Product interface
    name: request.displayName,
    baseUom: request.stockUnit ?? 'PCS',
    occStatus: 'draft',
  };
}

/** Edit-mode hydration from an enriched Product read. */
export function hydrateFromProduct(product: Product): ProductWizardFormData {
  return {
    sku: product.sku,
    displayName: product.displayName,
    subsidiaryId: product.subsidiaryId,
    categoryId: product.categoryId,
    includeChildren: product.includeChildren ?? false,
    // DD!21 — hydrated because the form schema requires the field and the
    // readiness preview reads it, NOT because an edit save round-trips it:
    // `toUpdateProductRequest` is isActive-free under D-3 and the edit seat
    // renders no Switch. On edit this value is carried, displayed nowhere in
    // the wizard, and written by nothing.
    isActive: product.isActive,
    unitsTypeId: product.unitsTypeId,
    stockUnit: product.stockUnit,
    purchaseUnit: product.purchaseUnit,
    saleUnit: product.saleUnit,
    lengthMm: product.lengthMm ?? null,
    widthMm: product.widthMm ?? null,
    heightMm: product.heightMm ?? null,
    weightKg: product.weightKg ?? null,
    fitmentMakes: product.fitment?.makes ?? [],
    fitmentModels: product.fitment?.models ?? [],
    fitmentYears: (product.fitment?.years ?? []).map((year, index) => ({
      id: `year-${index}`,
      yearFrom: year.yearFrom,
      yearTo: year.yearTo ?? null,
      variant: year.variant ?? '',
    })),
    variant: product.variant ?? '',
    extractYear: product.extractYear ?? null,
    faSubCategory: product.faSubCategory ?? '',
    ageStartYear: product.ageStartYear ?? null,
    ageEndYear: product.ageEndYear ?? null,
    mpn: product.mpn ?? '',
    countryOfManufacture: product.countryOfManufacture ?? '',
    salesDescription: product.salesDescription ?? '',
    purchaseDescription: product.purchaseDescription ?? '',
    customsDescription: product.customsDescription ?? '',
    oemNo: product.oemNo ?? '',
    upcCode: product.upcCode ?? '',
    customerPartNumbers: (product.customerPartNumbers ?? []).map((pn) => ({
      id: pn.id,
      customerId: pn.customerId,
      customerName: pn.customerName,
      partNumber: pn.partNumber,
      description: pn.description ?? '',
    })),
    sparePartNumbers: (product.sparePartNumbers ?? []).map((pn) => ({
      id: pn.id,
      partNumber: pn.partNumber,
      description: pn.description ?? '',
    })),
    vendorLines: (product.vendorLines ?? []).map((line) => ({
      id: line.id,
      vendorId: line.vendorId,
      vendorName: line.vendorName,
      vendorSku: line.vendorSku ?? '',
      unitCost: line.unitCost,
      currency: line.currency,
      orderUnit: line.orderUnit ?? null,
      isPrimary: line.isPrimary,
      leadTimeDays: line.leadTimeDays ?? null,
      isExisting: true,
    })),
    images: (product.images ?? []).map((image) => ({
      slot: image.slot,
      url: image.url,
      caption: image.caption ?? '',
    })),
    costingMethod: (product.costingMethod ?? null) as ProductCostingMethod | null,
    basePrice: product.basePrice ?? null,
    currency: product.currency ?? 'AUD',
    minQty: product.minQty ?? null,
    maxQty: product.maxQty ?? null,
    itemDefinedCost: product.itemDefinedCost ?? null,
    minQtyUnit: product.minQtyUnit ?? '',
    maxQtyUnit: product.maxQtyUnit ?? '',
    itemDefinedCostUnit: product.itemDefinedCostUnit ?? '',
    accounting: {
      incomeAccountId: product.accounting?.incomeAccountId ?? '',
      cogsAccountId: product.accounting?.cogsAccountId ?? '',
      assetAccountId: product.accounting?.assetAccountId ?? '',
      gainLossAccountId: product.accounting?.gainLossAccountId ?? '',
      priceVarianceAccountId: product.accounting?.priceVarianceAccountId ?? '',
      quantityVarianceAccountId: product.accounting?.quantityVarianceAccountId ?? '',
      exchangeRateVarianceAccountId: product.accounting?.exchangeRateVarianceAccountId ?? '',
      customerReturnVarianceAccountId: product.accounting?.customerReturnVarianceAccountId ?? '',
      vendorReturnVarianceAccountId: product.accounting?.vendorReturnVarianceAccountId ?? '',
      dropshipExpenseAccountId: product.accounting?.dropshipExpenseAccountId ?? '',
      deferredRevenueAccountId: product.accounting?.deferredRevenueAccountId ?? '',
      vendorBillPriceVarianceAccountId: product.accounting?.vendorBillPriceVarianceAccountId ?? '',
      vendorBillQuantityVarianceAccountId:
        product.accounting?.vendorBillQuantityVarianceAccountId ?? '',
      vendorBillExchangeRateVarianceAccountId:
        product.accounting?.vendorBillExchangeRateVarianceAccountId ?? '',
      intercompanyIncomeAccountId: product.accounting?.intercompanyIncomeAccountId ?? '',
      intercompanyCogsAccountId: product.accounting?.intercompanyCogsAccountId ?? '',
      taxScheduleId: product.accounting?.taxScheduleId ?? '',
    },
  };
}
